/** Shared app primitives in the elevated PhishFence-emerald system: warm,
 * airy, and polished (Stripe/Resend energy). White card surfaces on the larger
 * 2xl radius, soft layered shadows, hairline borders, smooth easing, and
 * brand-700 as the WCAG-safe accent. Used across the authenticated dashboard. */
import { clsx } from 'clsx';
import type { LucideIcon } from 'lucide-react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

// Shared field styling so inputs, textareas, and selects all carry the same
// white surface, slate-300 hairline, larger radius, and brand-500 focus ring
// with a soft offset (design guide §5).
const FIELD_CLASS =
  'w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition duration-200 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:ring-offset-0';

export function Button({
  className,
  variant = 'primary',
  icon: Icon,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'destructive';
  /** Optional leading lucide icon, rendered at 16px. */
  icon?: LucideIcon;
}) {
  return (
    <button
      className={clsx(
        'group inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 focus-visible:ring-offset-2 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0',
        variant === 'primary' &&
          'bg-brand-700 text-white shadow-soft-brand hover:bg-brand-800 hover:shadow-soft-md',
        variant === 'ghost' &&
          'border border-slate-300 bg-white font-medium text-slate-700 shadow-sm hover:border-slate-400 hover:bg-slate-50',
        variant === 'destructive' &&
          'border border-red-200 bg-white font-medium text-red-600 shadow-sm hover:border-red-300 hover:bg-red-50',
        className,
      )}
      {...props}
    >
      {Icon && <Icon className="h-4 w-4" strokeWidth={2} aria-hidden />}
      {children}
    </button>
  );
}

export function Input({
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={clsx(FIELD_CLASS, className)} {...props} />;
}

/** Multi-line text field sharing the Input treatment (design guide §5). */
export function Textarea({
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={clsx(FIELD_CLASS, className)} {...props} />;
}

/** Select field sharing the Input treatment plus one consistent custom chevron
 * (via the `.select-field` utility), so every dropdown looks identical and
 * never shows the browser's native up/down indicator (design guide §5). */
export function Select({
  className,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={clsx(FIELD_CLASS, 'select-field', className)} {...props} />
  );
}

/** The canonical labeled-control wrapper: a small uppercase-weight label above
 * an Input/Select/Textarea. Use this for EVERY labeled form control so the
 * label style, spacing, and field sizing are identical across every page. */
export function Field({
  label,
  htmlFor,
  hint,
  children,
  className,
}: {
  label: ReactNode;
  htmlFor?: string;
  /** Optional helper text under the control. */
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('space-y-1.5', className)}>
      <label
        htmlFor={htmlFor}
        className="block text-xs font-semibold text-slate-700"
      >
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

/** Elevated card surface (white, 2xl radius, soft layered shadow, hairline
 * border). Pass `interactive` for the hover-lift treatment on clickable cards. */
export function Card({
  children,
  className,
  interactive = false,
}: {
  children: ReactNode;
  className?: string;
  interactive?: boolean;
}) {
  return (
    <div
      className={clsx('card p-6', interactive && 'card-interactive', className)}
    >
      {children}
    </div>
  );
}

/** Section or card heading in DM Sans semibold with an optional leading icon
 * in a soft brand-tinted chip (design guide §3). Defaults to the card-title
 * scale; pass `className` to adjust size where a page needs a larger heading. */
export function CardTitle({
  children,
  className,
  icon: Icon,
}: {
  children: ReactNode;
  className?: string;
  icon?: LucideIcon;
}) {
  return (
    <h2
      className={clsx(
        'flex items-center gap-2.5 font-display text-base font-semibold text-slate-900',
        className,
      )}
    >
      {Icon && (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-600/10">
          <Icon className="h-4 w-4" strokeWidth={2} aria-hidden />
        </span>
      )}
      {children}
    </h2>
  );
}

/** The consistent page-header block used across every authenticated view: an
 * optional brand-tinted icon, a brand-700 mono-uppercase eyebrow, the DM Sans
 * page title, and an optional actions slot rendered to the right (§3, §4). */
export function PageHeader({
  eyebrow,
  title,
  actions,
  icon: Icon,
}: {
  eyebrow: string;
  title: ReactNode;
  actions?: ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex items-center gap-4">
        {Icon && (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 shadow-sm ring-1 ring-inset ring-brand-600/10">
            <Icon className="h-5 w-5" strokeWidth={2} aria-hidden />
          </span>
        )}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-700">
            {eyebrow}
          </p>
          <h1 className="mt-1.5 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {title}
          </h1>
        </div>
      </div>
      {actions}
    </header>
  );
}

/** KPI tile: a uniform white card with the big DM Sans value, an uppercase
 * label, a small monochrome icon, and an optional trend/delta line. Every tile
 * looks identical (no per-metric card tinting) so a row of stats reads as one
 * cohesive set; the label already names what each measures. `tone` is accepted
 * for API compatibility but intentionally does not colorize the whole card. */
export function Stat({
  label,
  value,
  icon: Icon,
  trend,
}: {
  label: string;
  value: number | string;
  icon?: LucideIcon;
  /** Optional delta rendered under the value, e.g. a rate or the raw counts. */
  trend?: ReactNode;
  /** Deprecated no-op kept so existing call sites compile; tiles are uniform. */
  tone?: 'brand' | 'good' | 'warn' | 'bad' | 'neutral';
}) {
  return (
    <div className="card px-5 py-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </p>
        {Icon && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-500/10">
            <Icon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
          </span>
        )}
      </div>
      <p className="mt-2.5 font-display text-[1.75rem] font-bold leading-none tracking-tight tabular-nums text-slate-900">
        {value}
      </p>
      {trend && (
        <p className="mt-1.5 text-xs font-medium text-slate-500">{trend}</p>
      )}
    </div>
  );
}

/** The one responsive grid for a row of KPI tiles. Using this everywhere means
 * tile width and gaps are identical on every page (two-up on mobile, four-up
 * from `sm`). Do not hand-roll the grid classes per page. */
export function StatGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('grid grid-cols-2 gap-4 sm:grid-cols-4', className)}>
      {children}
    </div>
  );
}

/** Compact sub-tile for metrics INSIDE a panel or card (label + value + an
 * optional sub line). One shared implementation so every in-panel metric is
 * sized identically wherever it appears (Sender Health, Warmup, etc.). */
export function MiniStat({
  label,
  value,
  sub,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className="mt-1 font-display text-lg font-bold tabular-nums text-slate-900">
        {value}
      </p>
      {sub != null && (
        <p className="mt-0.5 font-mono text-[11px] text-slate-400">{sub}</p>
      )}
    </div>
  );
}

/** Status chip in the elevated tonal style. Semantic variants map to
 * red/amber/green/blue/slate/brand for state (sent/replied/bounced/etc.).
 * Pass `dot` for a leading status dot or `icon` for a leading lucide glyph. */
export function Badge({
  children,
  variant = 'neutral',
  className,
  dot = false,
  icon: Icon,
}: {
  children: ReactNode;
  variant?: 'good' | 'warn' | 'bad' | 'info' | 'neutral' | 'brand';
  className?: string;
  dot?: boolean;
  icon?: LucideIcon;
}) {
  const palette: Record<string, string> = {
    good: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    warn: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    bad: 'bg-red-50 text-red-700 ring-red-600/20',
    info: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    neutral: 'bg-slate-100 text-slate-600 ring-slate-500/20',
    brand: 'bg-brand-50 text-brand-700 ring-brand-600/20',
  };
  const dotColor: Record<string, string> = {
    good: 'bg-emerald-500',
    warn: 'bg-amber-500',
    bad: 'bg-red-500',
    info: 'bg-blue-500',
    neutral: 'bg-slate-400',
    brand: 'bg-brand-500',
  };
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset',
        palette[variant],
        className,
      )}
    >
      {dot && (
        <span
          className={clsx('h-1.5 w-1.5 rounded-full', dotColor[variant])}
          aria-hidden
        />
      )}
      {Icon && <Icon className="h-3 w-3" strokeWidth={2.5} aria-hidden />}
      {children}
    </span>
  );
}

/** A card with a CardTitle header and a body, reducing the repeated
 * `<Card><CardTitle/>…</Card>` pattern across pages. */
export function SectionCard({
  title,
  icon,
  action,
  children,
  className,
  bodyClassName,
}: {
  title: ReactNode;
  icon?: LucideIcon;
  /** Optional control rendered to the right of the title (e.g. a button). */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <Card className={className}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <CardTitle icon={icon}>{title}</CardTitle>
        {action}
      </div>
      <div className={clsx('mt-4', bodyClassName)}>{children}</div>
    </Card>
  );
}

/** Centered empty state: a lucide icon in a soft brand-tinted circle, a title,
 * a supporting description, and an optional action. Used for every empty list. */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'card flex flex-col items-center justify-center px-8 py-14 text-center',
        className,
      )}
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-600/10">
        <Icon className="h-7 w-7" strokeWidth={1.75} aria-hidden />
      </span>
      <h3 className="mt-4 font-display text-base font-semibold text-slate-900">
        {title}
      </h3>
      {description && (
        <p className="mt-1.5 max-w-sm text-sm text-slate-500">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** Shimmer loading block. Compose several to sketch the shape of a page while
 * it loads instead of showing bare "Loading" text. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={clsx('skeleton rounded-lg', className)}
      aria-hidden
    />
  );
}
